import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { apiRequest } from "../../../../common/common";
import { LoadingPage } from "../../../../common/LoadingPage";
import DataTable from "../../../../common/DataTable";
import moment from "moment";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";

const PointInTime = ({
    projectId,
    getPointInTime,
    pointData
}) => {
    const { user, roles } = useSelector((state) => state.users);
    const isUser = roles.find((a) => a.id === user?.role_id);
    const [searchingText, setSearchText] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [rowsPerPage, setRowPerPage] = useState(10);
    const [gridApi, setGridApi] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [pointId, setPointId] = useState(null);
    const [pointName, setPointName] = useState(`Point In Time ${moment().format('YYYY-MM-DD hh:mm A')}`);
    const [pointOpen, setPointOpen] = useState(false);
    const [defaultPoint, setDefaultPoint] = useState(false);
    const [pointNote, setPointNote] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (data !== null) {
            setPointNote(data.note);
            setPointName(data?.name);
            setDefaultPoint(data?.is_default);
        }
    }, [data]);

    const colDefs = [
        {
            headerName: 'PointId',
            field: 'pointin_time_id',
            hide: true,
            filter: false,
            resizable: true,
        },
        {
            field: '',
            checkboxSelection: true,
            resizable: false,
            sortable: false,
            width: 50
        },
        {
            headerName: 'Actions',
            field: 'action',
            width: 140,
            filter: false,
            sortable: false,
            resizable: true
        },
        {
            headerName: 'Name',
            field: 'name',
            width: 270,
            hide: false,
            filter: false,
            resizable: true,
            suppressMenu: true,
        },
        {
            headerName: 'Default',
            field: 'is_default',
            width: 100,
            hide: false,
            filter: false,
            resizable: true,
            suppressMenu: true,
            cellRenderer: (params) => {
                return params?.data.is_default ? 'Yes' : 'No'
            }
        },
        {
            headerName: 'Created By',
            field: 'created_user',
            width: 150,
            hide: false,
            filter: false,
            resizable: true,
            suppressMenu: true,
            cellRenderer: (params) => {
                return `${params?.data?.created_user?.first_name} ${params?.data?.created_user?.last_name !== null ? params?.data?.created_user?.last_name : ''}`
            }
        },
        {
            headerName: 'Create Date/Time',
            field: 'created_at',
            width: 150,
            hide: false,
            filter: false,
            resizable: true,
            suppressMenu: true,
            cellRenderer: (params) => params?.data && (
                <span style={{ color: !params?.data?.is_deleted ? 'black' : 'red' }}>
                    {moment(params?.data?.created_at).format('DD-MMM-YYYY')}
                </span>
            ),
        },
        {
            headerName: 'Note',
            field: 'note',
            width: 122,
            hide: false,
            filter: false,
            resizable: true,
            sortable: false,
            suppressMenu: true,
            cellRenderer: (params) => params?.data && (
                <span>
                    {params?.data?.note || '--'}
                </span>
            ),
        },
    ];

    const onPageSizeChanged = (event) => {
        if (event.apiRequest) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const updatedColumns = useMemo(() => {
        let column = colDefs;
        if (column.length > 0) {
            return column?.map((col) => {
                switch (col.field) {
                    case 'action':
                        return {
                            ...col,
                            hide:  isUser?.code === 'orgGuestUser',
                            cellRenderer: (params) => params?.data && (
                                <Box style={{ display: 'flex', alignItems: 'center' }}>
                                    <EditOutlined
                                        color="primary"
                                        style={{ cursor: 'pointer', color: selectedData?.includes(params?.data?.pointin_time_id) ? '' : 'gray' }}
                                        onClick={() => {
                                            if (selectedData?.includes(params?.data?.pointin_time_id)) {
                                                setPointOpen(true);
                                                setData(params?.data);
                                                setPointId(params?.data?.pointin_time_id);
                                            }
                                        }}
                                    />
                                    <DeleteOutline
                                        color="secondary"
                                        style={{ cursor: 'pointer', color: selectedData?.includes(params?.data?.pointin_time_id) ? '' : 'gray' }}
                                        onClick={() => {
                                            if (selectedData?.includes(params?.data?.pointin_time_id)) {
                                                setDeleteOpen(true);
                                                setPointId(params?.data?.pointin_time_id);
                                            }
                                        }}
                                    />
                                </Box>
                            ),
                        }
                    default:
                        return col;
                }
            });
        }
        return column;
    }, [colDefs, selectedData]);

    useEffect(() => {
        if (gridApi) {
            gridApi.setGridOption(
                "quickFilterText",
                searchingText,
            );
        }
    }, [searchingText]);

    return (
        <Box mt={1} ml={1}>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <Dialog
                open={pointOpen}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Update Point In Time</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14 }}>A Point in Time is a snapshot of the  financial health of the project at a specific date and time. Once created, it can be used to be compare financial data in history tables, reports, and item detail pages.</Typography>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }} mt={1}>Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={defaultPoint}
                                            onClick={(e) => {
                                                setDefaultPoint(e.target.checked)
                                            }}
                                        />}
                                    label="Set this as the default Point In Time for this project"
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item sm={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Name *</InputLabel>
                                <OutlinedInput
                                    label="Name *"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={pointName}
                                    onChange={(e) => {
                                        setPointName(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Note</InputLabel>
                                <OutlinedInput
                                    label="Note"
                                    id="standard-adornment-password"
                                    type='text'
                                    multiline
                                    minRows={3}
                                    maxRows={3}
                                    value={pointNote}
                                    onChange={(e) => {
                                        setPointNote(e.target.value)
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => {
                            setPointOpen(false);
                            setDefaultPoint(false);
                            setPointNote(null);
                            setData(null);
                            setPointName(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={async () => {
                            try {
                                setDataLoading(true);
                                const data = {
                                    "name": pointName,
                                    "note": pointNote,
                                    "type": "manual",
                                    "status": "active",
                                    "project_id": projectId,
                                    "created_by": user?.user_id,
                                    "organization_id": user?.organization?.organization_id,
                                    "is_default": defaultPoint
                                }
                                const response = await apiRequest('put', `/point-in-time/${pointId}`, data);
                                if (response.data) {
                                    setPointOpen(false);
                                    setDefaultPoint(false);
                                    setPointNote(null);
                                    setData(null);
                                    setPointName(null);
                                    getPointInTime();
                                    showNotification(response?.data?.message, 'success');
                                }
                                setDataLoading(false);
                            } catch (error) {
                                setDataLoading(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteOpen} fullWidth maxWidth="xs">
                <DialogContent>Are you sure you want to delete this one?</DialogContent>
                <DialogActions>
                    <Button size="small" variant="contained" color="secondary"
                        onClick={() => {
                            setPointId(null);
                            setDeleteOpen(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            try {
                                setDataLoading(true);
                                let response = await apiRequest('delete', `/point-in-time/${pointId}`);
                                if (response?.data) {
                                    setDataLoading(false);
                                    showNotification(response?.data?.message, 'success');
                                    setPointId(null);
                                    setDeleteOpen(false);
                                    getPointInTime();
                                }
                            } catch (error) {
                                setDataLoading(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} mb={1}>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>Point In Time</Typography>
                <Box>
                    <TextField
                        size="small"
                        id="outlined-search"
                        type="text"
                        placeholder="Search Results...."
                        style={{
                            background: '#ebeaea',
                        }}
                        InputProps={{
                            style: { height: 37 },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                searchingText && ( // Show the clear icon only if there's text in the field
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                setSearchText('');
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            ),
                        }}
                        value={searchingText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                    />
                </Box>
            </Box>
            <DataTable
                height="60vh"
                columnDefs={updatedColumns}
                rowData={pointData}
                rowsPerPage={rowsPerPage}
                onPaginationChanged={onPageSizeChanged}
                modelType="clientSide"
                onGridReady={(params) => {
                    setGridApi(params.api)
                }}
                onSelectionChanged={() => {
                    if (gridApi) {
                        const selectedNodes = gridApi.getSelectedNodes();
                        setSelectedData(selectedNodes.map((node) => node.data.pointin_time_id));
                    }
                }}
            />
        </Box>
    )
}

export default PointInTime;